import { TFunction } from 'i18next';
import { DataTableMeta, DateColumn, LimitedContent, Typography } from '@otp/ui';
import { CategoryField } from './columns/category';
import { NotificationPayload, User } from 'src/graphql/generated/graphql';
import clsx from 'clsx';
import { ActionsColumn } from './columns/actions';
import { DocumentNode } from 'graphql';
import { parseNotification } from 'src/components/notifications/utils/notification-parser';
import { Configure } from '@otp/icons';

export const getTableConfig = ({
  t,
  data,
  refetchDocuments,
  currentUser,
  hasNotificationWritePermission,
}: {
  t: TFunction;
  data: Partial<NotificationPayload>[];
  refetchDocuments: DocumentNode[];
  currentUser?: User;
  hasNotificationWritePermission?: boolean;
}) => [
  {
    name: 'eventType.category',
    label: t('notifications.list.columns.category'),
    options: {
      setCellProps: () => ({ className: '!max-w-sm' }),
      customBodyRender: (value: string, column: DataTableMeta) => {
        const entity = data[column.rowIndex];
        return (
          <CategoryField
            value={value}
            category={entity.eventType?.enum}
            seen={!!entity.seenAt}
          />
        );
      },
    },
  },
  {
    name: 'name',
    label: t('common.name'),
    options: {
      customBodyRender: (value: string, column: DataTableMeta) => {
        const entity = data[column.rowIndex];
        const { name } = parseNotification(entity, {
          t,
          currentUser: currentUser!,
        });

        return (
          <Typography
            variant="subtitle1"
            className={clsx({ 'font-bold': !entity.seenAt })}
          >
            {name}
          </Typography>
        );
      },
    },
  },
  {
    name: 'description',
    label: t('common.description'),
    options: {
      customBodyRender: (value: string, column: DataTableMeta) => {
        const entity = data[column.rowIndex];
        const { description } = parseNotification(entity, {
          t,
          currentUser: currentUser!,
        });

        return (
          <LimitedContent maxRows={2}>
            <div
              dangerouslySetInnerHTML={{ __html: description }}
              className={clsx({
                'font-bold': !entity.seenAt,
              })}
            />
          </LimitedContent>
        );
      },
    },
  },
  {
    name: 'createdAt',
    label: t('notifications.list.columns.created'),
    options: {
      sort: true,
      customBodyRender: (value: string, column: DataTableMeta) => {
        const entity = data[column.rowIndex];

        return (
          <div
            className={clsx({
              'font-bold': !entity.seenAt,
            })}
          >
            <DateColumn date={value} />
          </div>
        );
      },
    },
  },
  ...(hasNotificationWritePermission
    ? [
        {
          name: 'actions',
          label: t('common.actions'),
          options: {
            customBodyRender: (_: any, column: DataTableMeta) => {
              const entity = data[column.rowIndex];

              return (
                <ActionsColumn
                  entity={entity}
                  refetchDocuments={refetchDocuments}
                />
              );
            },
          },
        },
      ]
    : []),
];
